import React from 'react';
import { Target, TrendingUp, BarChart, Users, Zap, Search, Settings } from 'lucide-react';
import OverviewTab from './tabs/OverviewTab';
import DifficultyTab from './tabs/DifficultyTab';
import CompetitorsTab from './tabs/CompetitorsTab';
import OpportunitiesTab from './tabs/OpportunitiesTab';
import PricesTab from './tabs/PricesTab';
import RelatedTab from './tabs/RelatedTab';
import GeoTab from './tabs/GeoTab';
import InsightsPanel from './InsightsPanel';

const Dashboard = ({
    analysis,
    activeTab,
    setActiveTab,
    insights,
    loadingInsights,
    generateInsights,
    clientDomain,
    setClientDomain,
    showClientConfig,
    setShowClientConfig,
    isPro,
    aiConfig,
    setAiConfig,
    availableModels,
    fetchModels,
    loadingModels,
    onNewSearch
}) => {
    const tabs = [
        { id: 'overview', label: 'Panoramica', icon: BarChart },
        { id: 'difficulty', label: 'Competitività', icon: Zap },
        { id: 'competitors', label: 'Competitor', icon: Users },
        { id: 'opportunities', label: 'Opportunità', icon: Target },
        { id: 'prices', label: 'Prezzi', icon: TrendingUp },
        { id: 'related', label: 'Correlate', icon: Search },
        { id: 'geo', label: 'AI & GEO', icon: Zap },
    ];

    return (
        <div className="w-full max-w-6xl mx-auto p-6 animate-fade-in">
            {/* Header Widget */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border-l-4 border-blue-500">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-1">{analysis.keyword}</h1>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded">SERP Live Advanced</span>
                            <span className="bg-gray-100 px-2 py-1 rounded">{analysis.totalResults} Risultati</span>
                            <span className="bg-gray-100 px-2 py-1 rounded">{analysis.organicCount} Organici</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className={`p-4 rounded-lg border-2 flex items-center gap-3 ${analysis.serpDifficulty.score <= 3 ? 'border-green-200 bg-green-50' :
                            analysis.serpDifficulty.score <= 6 ? 'border-yellow-200 bg-yellow-50' :
                                'border-red-200 bg-red-50'
                            }`}>
                            <div className="text-center">
                                <div className="text-xs uppercase font-bold text-gray-500">Difficulty</div>
                                <div className={`text-2xl font-black ${analysis.serpDifficulty.score <= 3 ? 'text-green-600' :
                                    analysis.serpDifficulty.score <= 6 ? 'text-yellow-600' :
                                        'text-red-600'
                                    }`}>
                                    {analysis.serpDifficulty.score}/10
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={onNewSearch}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-bold border border-gray-200"
                        >
                            <Search size={18} />
                            Nuova Ricerca
                        </button>

                        {isPro && !clientDomain && !showClientConfig && (
                            <button
                                onClick={() => setShowClientConfig(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all shadow-md font-bold"
                            >
                                <Settings size={20} />
                                Configura Dominio Cliente
                            </button>
                        )}
                    </div>
                </div>

                {isPro && (showClientConfig || clientDomain) && (
                    <div className="mt-6 pt-6 border-t border-dashed border-gray-200 animate-slide-down">
                        {/* Dominio Section */}
                        <div className="flex justify-between items-center mb-3 px-1">
                            <h3 className="text-sm font-bold text-purple-900 uppercase">Configurazione Dominio Cliente (PRO)</h3>
                            {clientDomain && (
                                <button
                                    onClick={() => setShowClientConfig(!showClientConfig)}
                                    className="text-xs text-purple-600 hover:text-purple-800 font-bold underline"
                                >
                                    {showClientConfig ? 'Nascondi' : 'Modifica Dominio'}
                                </button>
                            )}
                        </div>

                        {(showClientConfig || !clientDomain) && (
                            <div className="flex flex-col md:flex-row gap-3 mb-6">
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        value={clientDomain}
                                        onChange={(e) => setClientDomain(e.target.value)}
                                        placeholder="Inserisci il dominio del tuo cliente (es: miosito.it)"
                                        className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 outline-none transition-all font-medium"
                                    />
                                </div>
                                <button
                                    onClick={() => setShowClientConfig(false)}
                                    className="bg-purple-600 text-white font-bold px-8 py-3 rounded-lg hover:bg-purple-700 transition-all shadow-md active:scale-95"
                                >
                                    Salva Dominio
                                </button>
                            </div>
                        )}

                        {clientDomain && !showClientConfig && (
                            <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <span className="text-purple-700 font-bold">Dominio Attivo:</span>
                                    <span className="bg-white px-2 py-0.5 rounded border border-purple-200 text-purple-900 font-mono text-sm">{clientDomain}</span>
                                </div>
                            </div>
                        )}

                        {/* AI Configuration Section */}
                        <div className="border-t border-purple-100 pt-6 mt-6">
                            <div className="flex items-center gap-2 mb-4 px-1">
                                <Zap className="text-purple-600" size={20} />
                                <h3 className="text-sm font-bold text-purple-900 uppercase">Area Configurazione AI</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1 px-1">AI Provider</label>
                                    <select
                                        value={aiConfig.provider}
                                        onChange={(e) => {
                                            const provider = e.target.value;
                                            setAiConfig({ ...aiConfig, provider, model: '' });
                                        }}
                                        className="w-full p-3 bg-white border-2 border-purple-100 rounded-lg focus:border-purple-500 outline-none transition-all font-medium appearance-none"
                                    >
                                        <option value="anthropic">Anthropic (Claude)</option>
                                        <option value="openai">OpenAI (ChatGPT)</option>
                                        <option value="google">Google (Gemini)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1 px-1">Chiave API</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="password"
                                            value={aiConfig.apiKeys[aiConfig.provider]}
                                            onChange={(e) => {
                                                const newKeys = { ...aiConfig.apiKeys, [aiConfig.provider]: e.target.value };
                                                setAiConfig({ ...aiConfig, apiKeys: newKeys });
                                            }}
                                            placeholder={`Chiave per ${aiConfig.provider}`}
                                            className="flex-1 p-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 outline-none transition-all font-medium"
                                        />
                                        <button
                                            onClick={() => fetchModels(aiConfig.provider)}
                                            disabled={loadingModels || !aiConfig.apiKeys[aiConfig.provider]}
                                            className={`p-3 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-all ${loadingModels ? 'animate-spin' : ''}`}
                                            title="Carica Modelli"
                                        >
                                            <Settings size={20} />
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1 px-1">Modello</label>
                                    <select
                                        value={aiConfig.model}
                                        onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
                                        className="w-full p-3 bg-white border-2 border-purple-100 rounded-lg focus:border-purple-500 outline-none transition-all font-medium appearance-none"
                                        disabled={loadingModels || (!availableModels[aiConfig.provider] || availableModels[aiConfig.provider].length === 0)}
                                    >
                                        <option value="">{loadingModels ? 'Caricamento...' : (!availableModels[aiConfig.provider] || availableModels[aiConfig.provider].length === 0) ? 'Carica i modelli ➔' : 'Seleziona Modello'}</option>
                                        {availableModels[aiConfig.provider]?.map(model => (
                                            <option key={model} value={model}>{model}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <p className="text-xs text-purple-600 font-medium px-1 italic flex items-center gap-1">
                                <Zap size={12} />
                                * Inserisci l'API Key e clicca sull'icona ingranaggio per ottenere la lista aggiornata dei modelli dal provider.
                            </p>
                        </div>

                        <p className="mt-6 text-xs text-gray-400 font-medium px-1">
                            * Inserendo il dominio del cliente, gli Insights AI si concentreranno sul suo posizionamento relativo.
                        </p>
                    </div>
                )}
            </div>

            <InsightsPanel
                insights={insights}
                loadingInsights={loadingInsights}
                generateInsights={generateInsights}
                analysis={analysis}
                setActiveTab={setActiveTab}
            />

            <div className="bg-white rounded-lg shadow-lg mb-6">
                <div className="flex border-b overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === tab.id
                                ? 'border-b-2 border-blue-500 text-blue-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            {tab.icon && <tab.icon size={18} />}
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="p-6">
                    {activeTab === 'overview' && <OverviewTab analysis={analysis} />}
                    {activeTab === 'difficulty' && <DifficultyTab analysis={analysis} />}
                    {activeTab === 'competitors' && <CompetitorsTab analysis={analysis} clientDomain={clientDomain} />}
                    {activeTab === 'opportunities' && <OpportunitiesTab analysis={analysis} />}
                    {activeTab === 'prices' && <PricesTab analysis={analysis} />}
                    {activeTab === 'related' && <RelatedTab analysis={analysis} />}
                    {activeTab === 'geo' && <GeoTab analysis={analysis} aiConfig={aiConfig} availableModels={availableModels} clientDomain={clientDomain} />}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
